import { Button } from "@/components/generic/Button";
import { Checkbox } from "@/components/generic/Checkbox";
import { User } from "@/types/auth";
import { api } from "@/utils/api";
import { getLicenseData } from "@/utils/organization-helpers";
import { zodResolver } from "@hookform/resolvers/zod";
import { QueryClient } from "@tanstack/react-query";
import { ToOptions, useNavigate } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { Check } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Card } from "../Card";
import { NumberField } from "../generic/NumberField";
import { Separator } from "../generic/Separator";
import { SettingsContainer } from "../layout/SettingsContainer";

interface Props {
  backLinkOptions: ToOptions;
  context: { user: User; queryClient: QueryClient };
}

const schema = z.object({
  licenses: z.coerce.number<number>().min(1),
  acceptTerms: z.boolean().refine((value) => value === true, {
    message: "Du skal acceptere vilkårene for at fortsætte",
  }),
});

const MotionSettingsContainer = motion.create(SettingsContainer);

export function AddNewLicenses({ backLinkOptions, context }: Props) {
  const navigate = useNavigate();

  const { licensePrice } = getLicenseData(context.user!.organization);

  const {
    handleSubmit,
    control,
    watch,
    formState: { isValid },
  } = useForm({
    mode: "onSubmit",
    context: "addLicenses",
    resolver: zodResolver(schema),
    defaultValues: {
      licenses: 1,
    },
  });

  const licenses = watch("licenses");

  const [licenseConfirmation, setLicenseConfirmation] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const totalPrice = Number(licensePrice) * Number(licenses);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await api.post("apps/sales/settings/licenses/buy", {
        json: data.licenses,
      });

      setShowSuccess(true);
    } catch (err) {
      toast.error("Der opstod en uventet fejl", {
        description: "Vores teknikere har fået besked og arbejder på sagen.",
      });

      if (import.meta.env.DEV) console.error(err);
    }
  });

  return (
    <AnimatePresence mode="wait">
      {!showSuccess ? (
        <MotionSettingsContainer
          key="licenseForm"
          className="h-full"
          layoutId="card-addNewLicenses"
          backLinkOptions={backLinkOptions}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.5,
          }}
        >
          <div className="flex h-full w-full flex-col items-center space-y-6">
            <h3 className="text-theme-on-background text-base font-medium">
              {licenseConfirmation ? "Bekræft tilkøb" : "Tilkøb flere brugere"}
            </h3>
            {!licenseConfirmation && (
              <p className="text-theme-on-background-variant mb-14 text-center text-sm">
                Indtast det ønskede antal ekstra brugere. Prisen er{" "}
                {licensePrice} kr. pr. ekstra bruger.
              </p>
            )}
            <form
              key="licenseForm"
              className="flex h-full flex-col items-center justify-center transition-opacity"
              onSubmit={onSubmit}
            >
              <NumberField
                {...(licenseConfirmation
                  ? { type: "hidden", control }
                  : { control })}
                name="licenses"
                minValue={1}
                aria-label="Antal brugere"
                autoFocus
              />
              {licenseConfirmation && (
                <div className="flex w-[448px] flex-col items-center gap-y-3">
                  <Card
                    theme="border"
                    className="flex flex-wrap justify-between gap-y-8 p-8"
                  >
                    <span className="text-theme-on-background w-1/2 text-sm font-medium">
                      Antal ekstra brugere
                    </span>
                    <span className="text-theme-on-background w-1/2 text-right text-sm font-medium">
                      {licenses}
                    </span>
                    <span className="text-theme-on-background w-1/2 text-sm font-medium">
                      Pris pr. ekstra bruger
                    </span>
                    <span className="text-theme-on-background w-1/2 text-right text-sm font-medium">
                      {licensePrice} kr.
                    </span>
                    <Separator />
                    <span className="text-theme-on-background w-1/2 text-base font-medium">
                      Total
                    </span>
                    <span className="text-theme-on-background w-1/2 text-right text-base font-medium">
                      {totalPrice} kr.
                    </span>
                  </Card>
                  <Controller
                    name="acceptTerms"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <Checkbox
                        isSelected={field.value}
                        onChange={field.onChange}
                        name="acceptTerms"
                        aria-label="Accepter vilkår"
                        className="gap-4"
                      >
                        <span className="text-theme-on-background-variant line-clamp-3 grow text-xs leading-5">
                          Jeg accepterer{" "}
                          <a
                            className="text-theme-on-background hover:text-theme-active hover:underline"
                            href="https://lassox.com/terms-conditions"
                            target="_blank"
                            onClick={(e) => e.stopPropagation()}
                          >
                            betingelserne
                          </a>
                          , og at der tillægges et beløb på {totalPrice} kr. til
                          mit månedlige abonnement.
                        </span>
                      </Checkbox>
                    )}
                  />
                </div>
              )}
              <Button
                type={licenseConfirmation ? "submit" : "button"}
                onPress={() =>
                  licenseConfirmation
                    ? setShowSuccess(true)
                    : setLicenseConfirmation(true)
                }
                isDisabled={licenseConfirmation && !isValid}
                intent="primary"
                size="lg"
                className="mt-auto justify-self-end"
              >
                {licenseConfirmation ? "Bekræft tilkøb" : "Tilkøb"}
              </Button>
            </form>
          </div>
        </MotionSettingsContainer>
      ) : (
        <motion.div
          key="formSuccess"
          className="absolute inset-0 flex flex-col items-center justify-center transition-opacity"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.5,
            delay: 0.5,
          }}
        >
          <Check className="mt-auto size-20 text-green-600 drop-shadow-[0px_0px_19px_14px_rgba(188,188,188,0.25)] dark:text-green-400" />
          <p className="text-theme-on-background font-medium">
            Nye brugere tilkøbt
          </p>
          <div className="mt-auto mb-32 flex gap-x-4">
            <Button
              size="lg"
              intent="secondary"
              onPress={() => navigate(backLinkOptions)}
            >
              Okay
            </Button>
            <Button
              size="lg"
              intent="primary"
              onPress={() => navigate({ to: "/settings/users/invite-new" })}
            >
              Inviter bruger
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
